import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import localforage from 'localforage';
import { JS0N } from './ui/LoadingIndicator';
import { showGlassToast } from './ui/GlassToast';
import { getPushInstruction, requestPushToken, refreshPushTokenIfGranted, PUSH_STATUS } from '../push/pushManager';
import { API_URL } from '../config/endpoints';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setTokenState] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [referalInfo, setReferalInfo] = useState(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    // Загрузим sound из localStorage (можно тоже перенести в localforage, если нужно)
    return localStorage.getItem('sound') === 'enabled';
  });
  const [filters, setFilters] = useState(null);
  const [push, setPush] = useState();
  const [notifToken, setNotifToken] = useState();
  const [trackingItems, setTrackingItems] = useState([]);
  const [devices, setDevices] = useState([]);          // именно список объектов
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const tokenRef = useRef(token);
  const refreshPromiseRef = useRef(null);
  const profileRefreshPromiseRef = useRef(null);
  const lastProfileRefreshTsRef = useRef(0);
  const deviceIdRef = useRef(null);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const setToken = (newToken) => {
    tokenRef.current = newToken;
    setTokenState(newToken);
  };
  const apiUrl = API_URL;

  const parseApiError = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      if (data?.detail && typeof data.detail === 'object') {
        return data.detail.message || data.detail.code || fallbackMessage;
      }
      return data?.detail || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const parseAuthValidationError = async (response) => {
    try {
      const data = await response.json();
      const detail = data?.detail;
      if (detail && typeof detail === 'object') {
        return {
          detail: detail.message || detail.code || 'Не удалось проверить сессию',
          code: detail.code || null,
        };
      }
      return {
        detail: detail || 'Не удалось проверить сессию',
        code: null,
      };
    } catch {
      return {
        detail: 'Не удалось проверить сессию',
        code: null,
      };
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const normalizeDeviceId = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }
    if (!/^[A-Za-z0-9._:-]{8,128}$/.test(normalized)) {
      return null;
    }
    return normalized;
  };

  const generateDeviceId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const randomPart = Math.random().toString(36).slice(2, 12);
    return `dev-${Date.now().toString(36)}-${randomPart}`;
  };

  const ensureDeviceId = async () => {
    if (deviceIdRef.current) {
      return deviceIdRef.current;
    }
    const stored = normalizeDeviceId(await localforage.getItem('device_id'));
    if (stored) {
      deviceIdRef.current = stored;
      return stored;
    }
    const created = generateDeviceId();
    await localforage.setItem('device_id', created);
    deviceIdRef.current = created;
    return created;
  };

  const withDeviceHeader = async (headers = {}) => {
    const nextHeaders = { ...(headers || {}) };
    const deviceId = await ensureDeviceId();
    if (deviceId) {
      nextHeaders['X-Device-Id'] = deviceId;
    }
    return nextHeaders;
  };

  const savePushTokenForCurrentDevice = async (fcmToken, authToken = tokenRef.current) => {
    if (!fcmToken || !authToken) {
      return null;
    }
    const headers = await withDeviceHeader({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    });
    const response = await fetch(`${apiUrl}/filters/update_enpoints`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ endpoint: fcmToken }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Не удалось сохранить токен уведомлений'));
    }
    const data = await response.json();
    if (data?.filters) {
      setFilters(data.filters);
    }
    setNotifToken(fcmToken);
    setPush(true);
    return data;
  };

  const clearPushTokenForCurrentDevice = async (authToken = tokenRef.current) => {
    if (!authToken) {
      setNotifToken(null);
      setPush(false);
      return null;
    }
    const headers = await withDeviceHeader({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    });
    const response = await fetch(`${apiUrl}/filters/update_push`, {
      method: 'PUT',
      headers,
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Не удалось очистить токен уведомлений'));
    }
    setNotifToken(null);
    setPush(false);
    return await response.json().catch(() => null);
  };

  const syncPushTokenIfGranted = async (authToken = tokenRef.current) => {
    try {
      const result = await refreshPushTokenIfGranted();
      if (result.ok && result.token) {
        await savePushTokenForCurrentDevice(result.token, authToken);
        return result.token;
      }
      setPush(false);
      if (result.status === PUSH_STATUS.DENIED) {
        await clearPushTokenForCurrentDevice(authToken).catch((err) => {
          console.warn('clear push token failed:', err);
        });
      }
      return null;
    } catch (error) {
      console.warn('sync push token failed:', error);
      setPush(false);
      return null;
    }
  };

  const ensurePushToken = async ({ showInstructions = true } = {}) => {
    const result = await requestPushToken();
    if (result.ok && result.token) {
      try {
        await savePushTokenForCurrentDevice(result.token);
        return result.token;
      } catch (error) {
        if (showInstructions) {
          showGlassToast(error?.message || 'Не удалось сохранить токен уведомлений', { variant: 'error', duration: 5200 });
        }
        return null;
      }
    }

    setPush(false);
    if (result.status === PUSH_STATUS.DENIED) {
      await clearPushTokenForCurrentDevice().catch((err) => {
        console.warn('clear denied push token failed:', err);
      });
    }
    if (showInstructions) {
      showGlassToast(getPushInstruction(result.status), { variant: 'error', duration: 7600 });
    }
    return null;
  };

  const clearAuthState = async () => {
    setIsAuthenticated(false);
    setToken(null);
    setUserInfo(null);
    setReferalInfo(null);
    await localforage.removeItem('token');
  };

  const checkTokenValidity = async (checkToken) => {
    try {
      const headers = await withDeviceHeader({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${checkToken}`
      });
      const response = await fetch(`${apiUrl}/pandoras-echo`, {
        method: 'POST',
        headers,
        body: JS0N.stringify(checkToken),
      });

      if (response.ok) {
        const responseData = await response.json();
        return { status: true, data: responseData };
      }

      const authError = await parseAuthValidationError(response);
      if (response.status === 401 || response.status === 403) {
        return {
          status: false,
          reason: 'auth_error',
          httpStatus: response.status,
          detail: authError.detail,
          code: authError.code,
        };
      }
      return {
        status: false,
        reason: 'server_error',
        httpStatus: response.status,
        detail: authError.detail,
        code: authError.code,
      };
    } catch (error) {
      console.error('Error validating token:', error);
      return {
        status: false,
        reason: 'network_error',
        detail: error?.message || 'network_error'
      };
    }
  };

  const validateToken = async (extToken = null, options = {}) => {
    const { logoutOnAuthFail = true } = options;
    const currentToken = extToken || tokenRef.current;
    if (!currentToken) {
      return { token: null, reason: 'missing_token' };
    }

    let validation = await checkTokenValidity(currentToken);
    if (validation.status && validation.data?.status) {
      const responseData = validation.data;
      setIsAuthenticated(true);
      setToken(responseData.token);
      setUserInfo(responseData.user_info);
      await localforage.setItem('token', responseData.token);
      return { token: responseData.token, reason: 'ok' };
    }

    const reason = validation.reason || 'auth_error';
    const tryStoredToken = async () => {
      const latestStoredToken = await localforage.getItem('token').catch(() => null);
      const candidates = [tokenRef.current, latestStoredToken]
        .filter((candidate, index, list) => (
          candidate
          && candidate !== currentToken
          && list.indexOf(candidate) === index
        ));
      for (const candidateToken of candidates) {
        validation = await checkTokenValidity(candidateToken);
        if (validation.status && validation.data?.status) {
          const responseData = validation.data;
          setIsAuthenticated(true);
          setToken(responseData.token);
          setUserInfo(responseData.user_info);
          await localforage.setItem('token', responseData.token);
          return { token: responseData.token, reason: 'ok' };
        }
      }
      return null;
    };

    if (reason === 'auth_error') {
      const storedRetry = await tryStoredToken();
      if (storedRetry) {
        return storedRetry;
      }
      if (validation.code === 'device_session_not_found') {
        await sleep(450);
        const delayedStoredRetry = await tryStoredToken();
        if (delayedStoredRetry) {
          return delayedStoredRetry;
        }
        const latestStoredToken = await localforage.getItem('token').catch(() => null);
        if (latestStoredToken && latestStoredToken !== currentToken) {
          return {
            token: null,
            reason,
            detail: validation.detail,
            httpStatus: validation.httpStatus,
            code: validation.code,
          };
        }
      }
    }

    if (reason === 'auth_error' && logoutOnAuthFail) {
      await clearAuthState();
    }

    return {
      token: null,
      reason,
      detail: validation.detail,
      httpStatus: validation.httpStatus,
      code: validation.code
    };
  };

  const refreshAccessToken = async () => {
    if (refreshPromiseRef.current) {
      return await refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      return await validateToken(null, { logoutOnAuthFail: false });
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  };

  const applyAuthEffects = async (responseData) => {
    setIsAuthenticated(true);
    setToken(responseData.token);
    setUserInfo(responseData.user_info);
    await localforage.setItem('token', responseData.token);
    await syncPushTokenIfGranted(responseData.token);
  };

  const handleAuthResponse = async (responseData, options = {}) => {
    const { showModalOnFail = true } = options;
    if (responseData && responseData.token) {
      await applyAuthEffects(responseData);
    } else if (showModalOnFail) {
      setShowModal(true);
    }
    return responseData;
  };

  const login = async (telegramData) => {
    try {
      telegramData.userAgent = navigator.userAgent;
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const serverResponse = await fetch(`${apiUrl}/check_telegram_auth`, {
        method: 'POST',
        headers,
        body: JSON.stringify(telegramData),
      });

      if (!serverResponse.ok) {
        throw new Error('Ошибка при обращении к API');
      }

      const responseData = await serverResponse.json();
      await handleAuthResponse(responseData);
      return responseData;
    } catch (error) {
      console.error('Ошибка авторизации через Telegram:', error);
      showGlassToast(`Ошибка авторизации: ${error.message}`, { variant: 'error', duration: 3600 });
      return null;
    }
  };

  const startBotHandshake = async (handshakeId, clientMeta = {}) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/bot-auth/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          handshake_id: handshakeId,
          client_meta: {
            ...(clientMeta || {}),
            userAgent: navigator.userAgent,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось зарегистрировать попытку авторизации через бота');
      }
      return data;
    } catch (error) {
      console.error('Ошибка при создании записи авторизации через бота:', error);
      throw error;
    }
  };

  const checkBotHandshakeStatus = async (handshakeId) => {
    try {
      const headers = await withDeviceHeader();
      const response = await fetch(`${apiUrl}/bot-auth/status?handshake_id=${encodeURIComponent(handshakeId)}`, { headers });
      if (response.status === 404) {
        return { status: 'not_found' };
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось получить статус авторизации через бота');
      }
      return data;
    } catch (error) {
      console.error('Ошибка проверки статуса авторизации через бота:', error);
      throw error;
    }
  };

  const finalizeBotHandshake = async (handshakeId) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/bot-auth/finalize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          handshake_id: handshakeId,
          userAgent: navigator.userAgent,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось завершить авторизацию через бота');
      }
      if (data.token) {
        await handleAuthResponse(data);
      } else if (data.detail && data.detail !== 'handshake_pending') {
        await handleAuthResponse(data);
      }
      return data;
    } catch (error) {
      console.error('Ошибка завершения авторизации через бота:', error);
      throw error;
    }
  };

  const sendEmailAuthCode = async (email) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/email-auth/send-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Не удалось отправить код'));
      }
      return await response.json();
    } catch (error) {
      console.error('Ошибка отправки email-кода:', error);
      throw error;
    }
  };

  const verifyEmailAuthCode = async (email, code, refClaimToken = null) => {
    try {
      const body = { email, code };
      if (typeof refClaimToken === 'string' && refClaimToken.trim()) {
        body.ref_claim_token = refClaimToken.trim();
      }

      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/email-auth/verify-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Не удалось подтвердить код'));
      }

      const responseData = await response.json();
      await handleAuthResponse(responseData, { showModalOnFail: false });
      return responseData;
    } catch (error) {
      console.error('Ошибка подтверждения email-кода:', error);
      throw error;
    }
  };

  const getProfileEmailAuth = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return await response.json();
  };

  const getProfileReferralLink = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/referral-link`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return await response.json();
  };

  const sendProfileEmailAuthCode = async (email) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return await response.json();
  };

  const verifyProfileEmailAuthCode = async (email, code) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    return await response.json();
  };

  const startProfileTelegramLink = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/telegram-link/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const getProfileTelegramLinkStatus = async (handshakeId) => {
    const response = await fetchWithAuth(
      `${apiUrl}/profile/telegram-link/status?handshake_id=${encodeURIComponent(handshakeId)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return await response.json();
  };

  const finalizeProfileTelegramLink = async (handshakeId) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/telegram-link/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handshake_id: handshakeId }),
    });
    const data = await response.json();
    if (data?.token) {
      await handleAuthResponse(data, { showModalOnFail: false });
    }
    return data;
  };

  const logout = async () => {
    await clearAuthState();
  };

  // Обёртка для запросов, которые при 401 обновляют токен
  // Возвращает response или выбрасывает ошибку
  const fetchWithAuth = async (url, options = {}, retry = true) => {
    const baseHeaders = await withDeviceHeader(options.headers || {});
    baseHeaders['Authorization'] = `Bearer ${tokenRef.current}`;
    const requestOptions = { ...options, headers: baseHeaders };

    let response = null;
    try {
      response = await fetch(url, requestOptions);
    } catch (networkError) {
      throw new Error('Ошибка сети. Проверьте интернет и попробуйте снова.');
    }

    if (response.status === 401 && retry) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult?.token) {
        const retryOptions = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            'Authorization': `Bearer ${refreshResult.token}`
          }
        };
        try {
          response = await fetch(url, retryOptions);
        } catch (networkError) {
          throw new Error('Ошибка сети при обновлении сессии. Попробуйте снова.');
        }
      } else if (refreshResult?.reason === 'network_error' || refreshResult?.reason === 'server_error') {
        throw new Error('Временная ошибка сети/сервера при проверке сессии. Попробуйте еще раз.');
      } else {
        await clearAuthState();
        throw new Error('Сессия истекла. Авторизуйтесь снова.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || response.statusText;
      throw new Error(errorMessage);
    }

    return response;
  };

  const getPrig = async () => {
    const response = await fetchWithAuth(`${apiUrl}/pandoras-trace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();
    setReferalInfo(data.referals);
  };

  const getWsTicket = async () => {
    const response = await fetchWithAuth(`${apiUrl}/ws-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const refreshUserInfo = async ({ force = false } = {}) => {
    const currentToken = tokenRef.current;
    if (!currentToken) {
      return { token: null, reason: 'missing_token' };
    }
    const now = Date.now();
    if (!force && now - lastProfileRefreshTsRef.current < 5000) {
      return { token: currentToken, reason: 'throttled' };
    }
    if (profileRefreshPromiseRef.current) {
      return await profileRefreshPromiseRef.current;
    }
    profileRefreshPromiseRef.current = (async () => {
      const result = await validateToken(currentToken, { logoutOnAuthFail: false });
      if (result?.token) {
        lastProfileRefreshTsRef.current = Date.now();
        return result;
      }
      if (result?.reason === 'auth_error') {
        await clearAuthState();
      }
      return result;
    })();
    try {
      return await profileRefreshPromiseRef.current;
    } finally {
      profileRefreshPromiseRef.current = null;
    }
  };

  const getPublicProfile = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const checkPublicChatHandle = async (chatHandle = '') => {
    const normalized = String(chatHandle || '').trim();
    const response = await fetchWithAuth(
      `${apiUrl}/profile/public/chat-handle/check?chat_handle=${encodeURIComponent(normalized)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return await response.json();
  };

  const updatePublicProfile = async (updates = {}) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/public`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates || {}),
    });
    return await response.json();
  };

  const getUsersPublicBatch = async (accountIds = []) => {
    const response = await fetchWithAuth(`${apiUrl}/users/public/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_ids: accountIds }),
    });
    return await response.json();
  };

  const getUserPublicProfile = async (accountId) => {
    const response = await fetchWithAuth(`${apiUrl}/users/${encodeURIComponent(accountId)}/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const uploadMedia = async ({ profile, file, applyToProfile = false }) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetchWithAuth(
      `${apiUrl}/media/upload?profile=${encodeURIComponent(profile)}&apply_to_profile=${applyToProfile ? 'true' : 'false'}`,
      {
        method: 'POST',
        body: formData,
      }
    );
    return await response.json();
  };

  const getChatState = async () => {
    const response = await fetchWithAuth(`${apiUrl}/chat/state`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const getChatMessages = async ({ limit = 40, beforeId = null, afterId = null } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (beforeId !== null && beforeId !== undefined) params.set('before_id', String(beforeId));
    if (afterId !== null && afterId !== undefined) params.set('after_id', String(afterId));
    const response = await fetchWithAuth(`${apiUrl}/chat/messages?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const syncChatMessages = async ({ afterId = 0, limit = 40 } = {}) => {
    const params = new URLSearchParams();
    params.set('after_id', String(afterId || 0));
    params.set('limit', String(limit || 40));
    const response = await fetchWithAuth(`${apiUrl}/chat/sync?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const markChatRead = async (lastReadMessageId = null) => {
    const response = await fetchWithAuth(`${apiUrl}/chat/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ last_read_message_id: lastReadMessageId }),
    });
    return await response.json();
  };

  const sendChatMessage = async ({
    body,
    replyToMessageId = null,
    clientMsgId = null,
    photoMediaId = null,
    photoThumbMediaId = null,
  }) => {
    const response = await fetchWithAuth(`${apiUrl}/chat/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        body,
        reply_to_message_id: replyToMessageId,
        client_msg_id: clientMsgId,
        photo_media_id: photoMediaId,
        photo_thumb_media_id: photoThumbMediaId,
      }),
    });
    return await response.json();
  };

  const searchPromoAssistant = async (query) => {
    const response = await fetchWithAuth(`${apiUrl}/chat/promo-assistant/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    return await response.json();
  };

  const getMarketItems = async ({
    limit = 30,
    beforeId = null,
    q = '',
    priceMin = '',
    priceMax = '',
    includeCount = true,
  } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit || 30));
    if (beforeId !== null && beforeId !== undefined) params.set('before_id', String(beforeId));
    if (String(q || '').trim()) params.set('q', String(q).trim());
    if (String(priceMin || '').trim()) params.set('price_min', String(priceMin).trim());
    if (String(priceMax || '').trim()) params.set('price_max', String(priceMax).trim());
    if (!includeCount) params.set('include_count', 'false');
    const response = await fetchWithAuth(`${apiUrl}/market/items?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const getMarketItemById = async (itemId) => {
    const response = await fetchWithAuth(`${apiUrl}/market/items/${encodeURIComponent(itemId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const getMarketItemsBySeller = async (sellerId, { limit = 30, beforeId = null } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit || 30));
    if (beforeId !== null && beforeId !== undefined) params.set('before_id', String(beforeId));
    const response = await fetchWithAuth(
      `${apiUrl}/market/seller/${encodeURIComponent(sellerId)}/items?${params.toString()}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return await response.json();
  };

  const getMyMarketItems = async ({ limit = 30, beforeId = null } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit || 30));
    if (beforeId !== null && beforeId !== undefined) params.set('before_id', String(beforeId));
    const response = await fetchWithAuth(`${apiUrl}/market/my/items?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const createMarketItem = async (payload) => {
    const response = await fetchWithAuth(`${apiUrl}/market/my/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    return await response.json();
  };

  const updateMarketItem = async (itemId, payload) => {
    const response = await fetchWithAuth(`${apiUrl}/market/my/items/${encodeURIComponent(itemId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    return await response.json();
  };

  const deleteMarketItem = async (itemId) => {
    const response = await fetchWithAuth(`${apiUrl}/market/my/items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const payWithBonus = async (days) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${apiUrl}/pandoras-pay-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days })
      });
      const data = await response.json();
      if (data?.status) {
        await refreshUserInfo({ force: true });
      }
      return data;
    } catch (error) {
      console.error('Ошибка при оплате бонусами:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };


  const payWithRegular = async (email, days, amount, promoCode = '') => {
    setIsLoading(true);
    try {
      const payload = { email, days, amount };
      const normalizedPromo = (promoCode || '').trim();
      if (normalizedPromo) {
        payload.promo_code = normalizedPromo;
      }
      const response = await fetchWithAuth(`${apiUrl}/pandoras-pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Ошибка при обычной оплате:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const searchSales = async (type, value) => {
    const response = await fetchWithAuth(`${apiUrl}/search-sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: type, value: value })
    });
    const data = await response.json();
    return data;
  };

  const getFilters = async () => {
    const response = await fetchWithAuth(`${apiUrl}/filters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    setFilters(data.filters);
  };

  const updateFilters = async (updatedFilters, notifToken) => {
    const sanitizedFilters = updatedFilters.map(({ id, user_id, ...rest }) => rest);
    const response = await fetchWithAuth(`${apiUrl}/filters/update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters: sanitizedFilters, endpoint: notifToken })
    });
    const data = await response.json();
    setFilters(data.filters);
  };

  const getLastSale = async (article) => {
    const response = await fetchWithAuth(`${apiUrl}/get_sale?article=${encodeURIComponent(article)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    return data.data_sale;
  };

  const getFeedbackSales = async ({ beforeId = null, limit = 1000 } = {}) => {
    const response = await fetchWithAuth(`${apiUrl}/feedback-sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ before_id: beforeId, limit })
    });
    const data = await response.json();
    return data.data_sale;
  };

  const toggleSound = () => {
    const newSoundState = !isSoundEnabled;
    setIsSoundEnabled(newSoundState);
    localStorage.setItem('sound', newSoundState ? 'enabled' : 'disabled');
  };


  /**
   * Создать новую запись для отслеживания
   * @param {Object} trackingData – поля модели TrackingList (id не нужен при создании)
   */
  const createTrackingItem = async (trackingData) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData),
      });
      const data = await response.json();
      // Если хочешь, можешь тут же подливать в state trackingItems
      if (data.status) {
        // Объединяем новые данные со старыми
        setTrackingItems((prev) => [...prev, data.data]);
      }
      return data;
    } catch (error) {
      console.error('Ошибка createTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Обновить запись для отслеживания
   * @param {Object} trackingData – поля модели TrackingList (обязательно id)
   */
  const updateTrackingItem = async (trackingData) => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData),
      });
      const data = await response.json();
      if (data.status && data.data) {
        // Обновляем в локальном стейте
        setTrackingItems((prev) => {
          return prev.map((item) => (item.id === data.data.id ? data.data : item));
        });
      }
      return data;
    } catch (error) {
      console.error('Ошибка updateTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Получить все отслеживаемые товары для пользователя
   */
  const getTrackingList = async () => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/get_all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.status && data.data) {
        setTrackingItems(data.data);
      }
      return data;
    } catch (error) {
      console.error('Ошибка getTrackingList:', error);
      throw error;
    }
  };

  /**
   * Удалить запись из отслеживания
   * @param {number} itemId – ID записи, которую хотим удалить
   */
  const deleteTrackingItem = async (itemId) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId }),
      });
      const data = await response.json();
      if (data.status) {
        // Удаляем из локального стейта
        setTrackingItems((prev) => prev.filter((item) => item.id !== itemId));
      }
      return data;
    } catch (error) {
      console.error('Ошибка deleteTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Запросить дополнительную инфу (историю цены и т.п.) 
   * @param {number} article – артикул товара
   */
  const parseTrackingItem = async (article) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Ошибка parseTrackingItem:', error);
      throw error;
    }
  };


  /**
 * Обновить поле pause для записи отслеживания
 * @param {number} itemId – ID записи, которую нужно обновить
 * @param {boolean} pause – новое значение для поля pause
 */
  const updateTrackingItemPause = async (itemId, pause) => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/update_pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, pause }),
      });
      const data = await response.json();
      if (data.status) {
        // Обновляем локальное состояние, заменяя соответствующую запись
        setTrackingItems((prev) =>
          prev.map((item) =>
            item.id === itemId ? { ...item, pause } : item
          )
        );
      }
      return data;
    } catch (error) {
      console.error('Ошибка updateTrackingItemPause:', error);
      throw error;
    }
  };
  /* ---------- DEVICES ---------- */
  const getDevices = async () => {
    try {
      const resp = await fetchWithAuth(`${apiUrl}/devices`);
      const data = await resp.json();
      if (data.status) {
        setDevices(data.devices_info[0]);              // <– только список
        setCurrentDeviceId(data.devices_info[1]);   // <– отдельный id
      }
    } catch (err) {
      console.error('Ошибка при получении устройств:', err);
    }
  };

  const deleteDevice = async (deviceId) => {
    try {
      await fetchWithAuth(`${apiUrl}/devices/${deviceId}`, { method: 'DELETE' });
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      showGlassToast(err.message || 'Не удалось удалить устройство', { variant: 'error', duration: 3200 });
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await ensureDeviceId();
        const storedToken = await localforage.getItem('token');
        if (storedToken) {
          const validation = await validateToken(storedToken, { logoutOnAuthFail: false });
          const newToken = validation?.token;
          if (!newToken) {
            if (validation?.reason === 'auth_error') {
              await clearAuthState();
            }
          } else {
            await syncPushTokenIfGranted(newToken);
          }
        }
      } catch (error) {
        showGlassToast(`Ошибка при инициализации: ${error?.message || error}. Перезагрузите приложение, если не поможет — напишите @salew_support`, { variant: 'error', duration: 5200 });
        // В случае ошибки мы ничего не делаем, просто продолжим
      } finally {
        // Гарантированно снимаем isLoading
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const syncOnResume = () => {
      if (document.visibilityState === 'hidden') return;
      if (!tokenRef.current) return;
      void refreshUserInfo();
    };
    const forceSyncOnPageShow = () => {
      if (!tokenRef.current) return;
      void refreshUserInfo({ force: true });
    };
    document.addEventListener('visibilitychange', syncOnResume);
    window.addEventListener('focus', syncOnResume);
    window.addEventListener('pageshow', forceSyncOnPageShow);
    return () => {
      document.removeEventListener('visibilitychange', syncOnResume);
      window.removeEventListener('focus', syncOnResume);
      window.removeEventListener('pageshow', forceSyncOnPageShow);
    };
  }, []);

  return (
    <AuthContext.Provider value={{
      isLoading,
      setIsLoading,
      isAuthenticated,
      token,
      userInfo,
      login,
      startBotHandshake,
      checkBotHandshakeStatus,
      finalizeBotHandshake,
      sendEmailAuthCode,
      verifyEmailAuthCode,
      getProfileEmailAuth,
      getProfileReferralLink,
      sendProfileEmailAuthCode,
      verifyProfileEmailAuthCode,
      startProfileTelegramLink,
      getProfileTelegramLinkStatus,
      finalizeProfileTelegramLink,
      refreshUserInfo,
      logout,
      showModal,
      setShowModal,
      getPrig,
      getWsTicket,
      getPublicProfile,
      checkPublicChatHandle,
      updatePublicProfile,
      getUsersPublicBatch,
      getUserPublicProfile,
      uploadMedia,
      getChatState,
      getChatMessages,
      syncChatMessages,
      markChatRead,
      sendChatMessage,
      searchPromoAssistant,
      getMarketItems,
      getMarketItemById,
      getMarketItemsBySeller,
      getMyMarketItems,
      createMarketItem,
      updateMarketItem,
      deleteMarketItem,
      referalInfo,
      payWithBonus,
      payWithRegular,
      isSoundEnabled,
      toggleSound,
      ensurePushToken,
      syncPushTokenIfGranted,
      searchSales,
      getFilters,
      updateFilters,
      filters,
      getFeedbackSales,
      getLastSale,
      trackingItems,
      createTrackingItem,
      updateTrackingItem,
      getTrackingList,
      deleteTrackingItem,
      parseTrackingItem,
      updateTrackingItemPause,
      devices,
      getDevices,
      deleteDevice,
      currentDeviceId
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
